import { IDate } from '@/interfaces/general/IDate.ts';
import { IInterconnector } from '@/interfaces/powerMarkets/maps/interconnectors/IInterconnector.ts';
import {
    IMap, IEuropeMap, IEuropeMapPayload, IMapSeriesData,
} from '@/interfaces/powerMarkets/maps/IMap.ts';
import { IMapDataItem } from '@/interfaces/powerMarkets/maps/IMapDataItem.ts';
import { IMapZonalData } from '@/interfaces/powerMarkets/maps/IMapZonalData.ts';
import { ISeriesData } from '@/interfaces/powerMarkets/ISeriesData.ts';
import { IBoaMapPayload } from '@/interfaces/powerMarkets/payloads/IBoaMapPayload.ts';
import { IConstraintsPayload } from '@/interfaces/powerMarkets/payloads/IConstraintsPayload.ts';
import { IMapZonalPushPayload } from '@/interfaces/powerMarkets/payloads/IMapZonalPushPayload.ts';
import { ApiPromise, useApi } from '@/api/useApi.ts';
import { IPlantChartReturn } from '@/interfaces/powerMarkets/charts/IPlantChartReturn.ts';

const apiCall = useApi();
const entsoeApiCall = useApi('entsoe');

function getInterconnectors (): ApiPromise<Array<IInterconnector>> {
    return apiCall.get<Array<IInterconnector>>({
        endpoint: '/interconnector',
    });
}

function getMap (): ApiPromise<IMap> {
    return apiCall.get<IMap>({
        endpoint: '/map',
    });
}

function getBoaMapData (params: IBoaMapPayload): ApiPromise<Array<IMapDataItem>> {
    const {
        day: date, month, year, period, isVolume, bidColour, offerColour,
    } = params;

    let subDirectory = `${isVolume}/${bidColour}/${offerColour}`.replaceAll('#', '%23');

    if (period) {
        subDirectory = `${date}/${month}/${year}/${period}/${subDirectory}`;
    }

    return apiCall.get<Array<IMapDataItem>>({
        endpoint: `/map/boa/${subDirectory}`,
    });
}

function getMapZonalData (params: IDate): ApiPromise<IMapZonalData> {
    const {
        day: date, month, year, period,
    } = params;

    return apiCall.get<IMapZonalData>({
        endpoint: `/mapzonal/${date}/${month}/${year}/${period}`,
    });
}

function getMapZonalDataSpecific<TResponse> (params: IMapZonalPushPayload): ApiPromise<TResponse> {
    return apiCall.post<IMapZonalPushPayload, TResponse>({
        endpoint: '/mapzonal',
        payload: params,
    });
}

async function getEuropeMapPage (payload?: IEuropeMapPayload): ApiPromise<ISeriesData | IMapSeriesData> {
    if (!payload) {
        return entsoeApiCall.get<IMapSeriesData>({
            endpoint: '/EuropeMapPage',
        });
    }
    return entsoeApiCall.post<IEuropeMapPayload, ISeriesData>({
        endpoint: '/EuropeMapPage',
        payload,
    });
}

async function getEuropeMap (): ApiPromise<Array<IEuropeMap>> {
    return entsoeApiCall.get<Array<IEuropeMap>>({
        endpoint: '/europeMap',
    });
}

async function getConstraints (payload: IConstraintsPayload): ApiPromise<IPlantChartReturn> {
    return apiCall.post<IConstraintsPayload, IPlantChartReturn>({
        endpoint: '/constraintData/getConstraints',
        payload,
    });
}

export const mapApi = {
    getBoaMapData,
    getInterconnectors,
    getMap,
    getMapZonalData,
    getMapZonalDataSpecific,
    getEuropeMapPage,
    getEuropeMap,
    getConstraints,
};
